import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

export function Tools({ selectedTool, lockState }: any) {
  const wrenchRef = useRef<THREE.Group>(null);
  const pickRef = useRef<THREE.Group>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      });
    };

    const handleMouseDown = () => setIsDragging(true);
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    if (wrenchRef.current) {
      wrenchRef.current.position.x = -2.5 + mousePos.x * 0.8;
      wrenchRef.current.position.y = -0.8 + mousePos.y * 0.6;
      wrenchRef.current.rotation.z = Math.atan2(mousePos.y, mousePos.x) - Math.PI / 3;
    }
  }, [mousePos]);

  useEffect(() => {
    if (pickRef.current) {
      pickRef.current.position.x = -1.5 + mousePos.x * 1.2;
      pickRef.current.position.y = 0.2 + mousePos.y * 0.8;
      pickRef.current.rotation.z = Math.atan2(mousePos.y, mousePos.x) - Math.PI / 6;
    }
  }, [mousePos]);

  return (
    <>
      {/* Tension Wrench */}
      <group ref={wrenchRef} position={[-2.5, -0.8, 0.95]}>
        {/* Handle */}
        <mesh castShadow>
          <boxGeometry args={[0.18, 0.1, 0.7]} />
          <meshStandardMaterial
            color="#1a1a1a"
            metalness={0.6}
            roughness={0.4}
          />
        </mesh>

        {/* Working end - L-shaped bend */}
        <mesh position={[0, -0.15, 0.4]} castShadow>
          <boxGeometry args={[0.14, 0.06, 0.3]} />
          <meshStandardMaterial
            color="#888888"
            metalness={0.92}
            roughness={0.1}
          />
        </mesh>

        {/* Tension indicator glow */}
        {lockState.rotation > 8 && (
          <mesh position={[0, -0.2, 0.2]} castShadow>
            <sphereGeometry args={[0.08, 16, 16]} />
            <meshStandardMaterial
              color="#22c55e"
              emissive="#22c55e"
              emissiveIntensity={0.6}
            />
          </mesh>
        )}
      </group>

      {/* Pick Tool */}
      <group ref={pickRef} position={[-1.5, 0.2, 1.0]}>
        {/* Handle - ergonomic */}
        <mesh castShadow>
          <boxGeometry args={[0.14, 0.12, 0.6]} />
          <meshStandardMaterial
            color="#2a2a2a"
            metalness={0.5}
            roughness={0.5}
          />
        </mesh>

        {/* Metal shaft */}
        <mesh position={[0, 0.05, 0.4]} castShadow>
          <cylinderGeometry args={[0.05, 0.05, 0.5, 16]} />
          <meshStandardMaterial
            color="#c0c0c0"
            metalness={0.95}
            roughness={0.08}
          />
        </mesh>

        {/* Pick tip - diamond shaped */}
        <mesh position={[0, 0, 0.7]} castShadow rotation={[Math.PI / 4, 0, 0]}>
          <octahedronGeometry args={[0.07, 0]} />
          <meshStandardMaterial
            color="#888888"
            metalness={0.9}
            roughness={0.1}
            emissive={selectedTool === 'pick' ? '#3b82f6' : '#000000'}
            emissiveIntensity={selectedTool === 'pick' ? 0.5 : 0}
          />
        </mesh>

        {/* Glow when active and over lock */}
        {selectedTool === 'pick' && lockState.rotation > 5 && (
          <mesh position={[0, 0, 0.7]} castShadow>
            <sphereGeometry args={[0.12, 16, 16]} />
            <meshStandardMaterial
              color="#3b82f6"
              emissive="#3b82f6"
              emissiveIntensity={0.4}
              transparent
              opacity={0.3}
            />
          </mesh>
        )}
      </group>
    </>
  );
}